import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { isFullAddress } from '@/components/dashboard_components/manage_application_dashboard/ApplicationUpdateModal/utils/isFullAddress';
import { buildStatusOptions } from '@/components/dashboard_components/manage_application_dashboard/ApplicationUpdateModal/utils/statusOptions';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import useContact from '@/hooks/useContact';
import usePartner from '@/hooks/usePartner';
import { ApplicationProps } from '@/models/Application';
import { ContactProps } from '@/models/Contact';
import { CountryProps } from '@/models/Country';
import { PartnerProps } from '@/models/Partner';
import { PartnerGroupProps } from '@/models/PartnerGroup';
import { SelectOptionProps } from '@/models/SelectOption';
import { StateProps } from '@/models/State';
import { ApplicationStatus, USCountryId } from '@/utils/constants';
import { formatErrorMessage } from '@/utils/errorHandlerUtil';

type Args = {
  open: boolean;
  applicationId: number | undefined;
  currentRowStatus: number | undefined;
};

export type ApplicationUpdateFormData = {
  partnerId: number | null;
  contactId: number | null;
  status: ApplicationStatus | null;
  additionalStickers: number | null;
};

const emptyForm: ApplicationUpdateFormData = {
  partnerId: null,
  contactId: null,
  status: null,
  additionalStickers: null,
};

export function useApplicationUpdateModalData({ open, applicationId, currentRowStatus }: Args) {
  const { allStates, allCountries } = useContext(ConfigContext);
  const { userType } = useContext(UsersContext);
  const { setAlert } = useContext(SnackbarContext);

  const { getOneApplication } = useApplication();
  const { getPartner, getPartnerGroup, getPartnersByPartnerGroupId } = usePartner();
  const { getAllContactsByPartnerId } = useContact();

  // server snapshot (read-only after load)
  const [applicationData, setApplicationData] = useState<ApplicationProps | null>(null);

  // editable form state
  const [formData, setFormData] = useState<ApplicationUpdateFormData>(emptyForm);

  const [partnerGroup, setPartnerGroup] = useState<PartnerGroupProps | null>(null);
  const [partners, setPartners] = useState<PartnerProps[]>([]);
  const [contacts, setContacts] = useState<ContactProps[]>([]);
  const [selectedPartnerGroupId, setSelectedPartnerGroupId] = useState<number | null>(null);

  const [loading, setLoading] = useState({
    app: false,
    partner: false,
    partnerGroup: false,
    partners: false,
    contacts: false,
  });

  // Guards: initialize form only once per application id (per open)
  const initializedForAppIdRef = useRef<number | null>(null);

  const resetAll = useCallback(() => {
    initializedForAppIdRef.current = null;

    setApplicationData(null);
    setFormData(emptyForm);

    setPartnerGroup(null);
    setPartners([]);
    setContacts([]);
    setSelectedPartnerGroupId(null);

    setLoading({
      app: false,
      partner: false,
      partnerGroup: false,
      partners: false,
      contacts: false,
    });
  }, []);

  const statusOptions = useMemo(
    () => buildStatusOptions(userType, currentRowStatus),
    [userType, currentRowStatus]
  );

  const additionalStickers = useMemo(
    () => formData.additionalStickers,
    [formData.additionalStickers]
  );

  // Fetch chain (open -> app -> partner -> partnerGroup -> partners -> contacts)
  useEffect(() => {
    if (!open) return;
    if (applicationId == null) return;
    if (allCountries.length === 0) return;

    let cancelled = false;

    void (async () => {
      try {
        setLoading((s) => ({ ...s, app: true }));

        const a = await getOneApplication(applicationId);
        if (cancelled || a == null) return;

        // ONLY set the snapshot here
        setApplicationData(a);

        setLoading((s) => ({ ...s, partner: true }));
        const p = await getPartner(a.partnerId);
        if (cancelled || p == null) return;

        setSelectedPartnerGroupId(p.partnerGroupId ?? null);
      } catch (err) {
        if (!cancelled) {
          setAlert({ severity: 'error', message: formatErrorMessage(err) });
        }
      } finally {
        if (!cancelled) {
          setLoading((s) => ({ ...s, app: false, partner: false }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, applicationId, allCountries.length, getOneApplication, getPartner, setAlert]);

  // Initialize editable form state ONCE when snapshot arrives
  useEffect(() => {
    if (!open) return;
    if (applicationData?.id == null) return;

    if (initializedForAppIdRef.current === applicationData.id) return;
    initializedForAppIdRef.current = applicationData.id;

    setFormData({
      partnerId: applicationData.partnerId ?? null,
      contactId: applicationData.applicationContactId ?? null,
      status: applicationData.status ?? null,
      additionalStickers: applicationData.additionalStickers ?? null,
    });
  }, [open, applicationData]);

  useEffect(() => {
    if (!open) return;
    if (selectedPartnerGroupId == null) return;

    let cancelled = false;

    void (async () => {
      try {
        setLoading((s) => ({ ...s, partnerGroup: true }));
        const pg = await getPartnerGroup(selectedPartnerGroupId);
        if (cancelled) return;
        setPartnerGroup(pg);
      } catch (err) {
        if (!cancelled) {
          setAlert({ severity: 'error', message: formatErrorMessage(err) });
        }
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, partnerGroup: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedPartnerGroupId, getPartnerGroup, setAlert]);

  useEffect(() => {
    if (!open) return;
    if (partnerGroup?.id == null) return;

    let cancelled = false;

    void (async () => {
      try {
        if (partnerGroup.id == null) return;
        setLoading((s) => ({ ...s, partners: true }));
        const pps = await getPartnersByPartnerGroupId(partnerGroup.id);
        if (cancelled) return;
        setPartners(pps ?? []);
      } catch (err) {
        if (!cancelled) {
          setAlert({ severity: 'error', message: formatErrorMessage(err) });
        }
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, partners: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, partnerGroup?.id, getPartnersByPartnerGroupId, setAlert]);

  useEffect(() => {
    if (!open) return;
    if (applicationData == null) return;
    if (partners.length === 0) return;

    let cancelled = false;

    void (async () => {
      try {
        setLoading((s) => ({ ...s, contacts: true }));
        const partnerIds = partners.map((p) => p.id).filter((id): id is number => id != null);

        const results = await Promise.all(partnerIds.map((id) => getAllContactsByPartnerId(id)));

        if (cancelled) return;
        setContacts(results.flat());
      } catch (err) {
        if (!cancelled) {
          setAlert({ severity: 'error', message: formatErrorMessage(err) });
        }
      } finally {
        if (!cancelled) setLoading((s) => ({ ...s, contacts: false }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, applicationData, partners, getAllContactsByPartnerId, setAlert]);

  const partnerOptions: SelectOptionProps[] | null = useMemo(() => {
    if (applicationData == null || partners.length === 0) return null;

    return partners
      .filter((p) => p?.id != null)
      .map((partner) => {
        const state = allStates.find((s: StateProps) => s.id === partner.stateId);
        const country = allCountries.find((c: CountryProps) => c.id === partner.countryId);

        const stateName = country?.id === USCountryId && state != null ? ` (${state.name})` : '';
        const countryName =
          country?.id != null && country.id !== USCountryId ? ` (${country.name})` : '';

        return {
          value: partner.id!.toString(),
          label: `${partner.partnerName}${stateName != null ? stateName : countryName}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [applicationData, partners, allStates, allCountries]);

  const contactOptions: SelectOptionProps[] | null = useMemo(() => {
    if (applicationData == null || contacts.length === 0) return null;

    const options: SelectOptionProps[] = [];

    // Always include the current saved login contact from the snapshot (so it appears even if address is incomplete)
    const current = contacts.find((c) => c.id === applicationData.applicationContactId);
    if (current?.id != null) {
      options.push({
        value: current.id.toString(),
        label: `${current.firstName} ${current.lastName} (${current.email})`,
      });
    }

    for (const c of contacts) {
      if (c.id == null) continue;
      if (!isFullAddress(c)) continue;

      const label = `${c.firstName} ${c.lastName} (${c.email})`;
      const exists = options.some((o) => o.value === c.id!.toString() || o.label === label);
      if (!exists) options.push({ value: c.id.toString(), label });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [applicationData, contacts]);

  // ergonomic setters
  const setPartnerId = useCallback(
    (partnerId: number | null) => setFormData((f) => ({ ...f, partnerId })),
    []
  );
  const setContactId = useCallback(
    (contactId: number | null) => setFormData((f) => ({ ...f, contactId })),
    []
  );
  const setStatus = useCallback(
    (status: ApplicationStatus | null) => setFormData((f) => ({ ...f, status })),
    []
  );
  const setAdditionalStickers = useCallback(
    (additionalStickersValue: number | null) =>
      setFormData((f) => ({
        ...f,
        additionalStickers: additionalStickersValue,
      })),
    []
  );

  return {
    applicationData,
    formData,

    // per-field setters (easy to pass to inputs)
    setPartnerId,
    setContactId,
    setStatus,
    setAdditionalStickers,

    partnerOptions,
    contactOptions,
    statusOptions,
    additionalStickers,

    loading,
    resetAll,
  };
}
